import { RoleSummary } from 'constants/roles';
import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('UsersService');

const baseUrl = environment.services.account.baseUrl;
const accountsUrl = baseUrl + environment.services.account.basePath;

export interface Account {
  id: number;
}

export interface Preferences {
  firstTimeLogin: boolean;
}

export interface User {
  id: number;
  account: Account;
  robotic: boolean;
  enabled: boolean;
  active: boolean;
  locked: boolean;
  username: string;
  createDate: string;
  updateDate: string;
  createdBy?: number;
  updatedBy: number;
  privs: string[];
  lastLoginDate: string;
  roles: RoleSummary[];
  firstName: string;
  lastName: string;
  preferences: Preferences;
  welcomeEmailDate?: string;
}

export interface ApiRole {
  id: number;
  name: string;
  displayName: string;
  legacy: boolean;
}

export interface UsersFilter {
  enabled?: boolean;
  accountId?: number;
  robotic?: boolean;
  username?: string;
}

export interface UsersParams {
  page: number;
  size: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  filters?: UsersFilter;
}

class UsersService {
  async getUsers(params: UsersParams): Promise<{data: User[], count: number}> {
    try {
      let apiUrl = `${accountsUrl}/users/?page=${params.page}&size=${params.size}`;
      
      if (params.sort) {
        const sortFields = params.sort.split(',');
        sortFields.forEach(field => {
          apiUrl += `&sort=${field.trim()}`;
        });
        if (params.direction) {
          apiUrl += `&direction=${params.direction}`;
        }
      }

      if (params.filters) {
        if (params.filters.enabled !== undefined) {
          apiUrl += `&enabled=${params.filters.enabled}`;
        }
        
        if (params.filters.accountId) {
          apiUrl += `&accountId=${params.filters.accountId}`;
        }
        
        if (params.filters.robotic !== undefined) {
          apiUrl += `&robotic=${params.filters.robotic}`;
        }
        
        if (params.filters.username && params.filters.username.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(params.filters.username.trim())}`;
        }
      }
      
      log.log('Calling API with URL:', apiUrl);
      
      const response = await apiService.makeRequest('get', apiUrl);
      const responseData = response.data;
      
      let total = 0;
      if (responseData && typeof responseData === 'object') {
        total = responseData.count || responseData.totalCount || responseData.total || 0;
      }
      
      let usersArray = [];
      
      if (Array.isArray(responseData)) {
        usersArray = responseData;
        total = response?.count || responseData.length;
      } else if (responseData && typeof responseData === 'object') {
        usersArray = responseData.data || responseData.users || responseData.results || responseData.items || responseData.content || [];
        
        if (!Array.isArray(usersArray) && responseData.id) {
          usersArray = [responseData];
        }
      }
      
      if (!Array.isArray(usersArray)) {
        throw new Error('Unexpected API response format');
      }
      
      return {
        data: usersArray,
        count: total
      };
    } catch (error) {
      log.error('Error fetching users:', error);
      throw error;
    }
  }

  async getRoles(): Promise<ApiRole[]> {
    try {
      let apiUrl = `${accountsUrl}/roles?legacy=false`;
      const response = await apiService.makeRequest('get', apiUrl);
      return response.data ?? response;
    } catch (error) {
      log.error('Error fetching roles:', error);
      throw error;
    }
  }

  async getUser(id: number): Promise<User> {
    try {
      const response = await apiService.makeRequest('get', `${accountsUrl}/users/${id}`);
      return response;
    } catch (error) {
      log.error(`Error fetching user with ID ${id}:`, error);
      throw error;
    }
  }

  async updateUser(user: User): Promise<User> {
    try {
      const response = await apiService.makeRequest('put', `${accountsUrl}/users/${user.id}`, user);
      
      // Invalidate cache for users list and this specific user
      apiService.invalidateCacheByPattern(`/users/?`);  // Clear users list cache
      apiService.invalidateCacheByPattern(`/users/${user.id}`); // Clear this user's cache
      
      return response.data;
    } catch (error) {
      log.error(`Error updating user with ID ${user.id}:`, error);
      throw error;
    }
  }

  async createUser(user: Omit<User, 'id'>): Promise<User> {
    try {
      const response = await apiService.makeRequest('post', `${accountsUrl}/users`, user);

      // Invalidate cache for users list
      apiService.invalidateCacheByPattern(`/users/?`);

      return response.data;
    } catch (error) {
      log.error('Error creating user:', error);
      throw error;
    }
  }

  async changeUserStatus(userId: number, enabled: boolean): Promise<User> {
    try {
      const response = await apiService.makeRequest('put', `${accountsUrl}/users/${userId}/status`, { enabled });

      apiService.invalidateCacheByPattern(`/users/${userId}`);
      apiService.invalidateCacheByPattern(`/users/?`);

      return response.data;
    } catch (error) {
      log.error(`Error changing status for user ${userId}:`, error);
      throw error;
    }
  }

  async migrateUser(userId: number, newAccountId: number): Promise<User> {
    try {
      const response = await apiService.makeRequest('put', `${accountsUrl}/users/${userId}/migrate`, { newAccount: newAccountId });

      apiService.invalidateCacheByPattern(`/users/${userId}`);
      apiService.invalidateCacheByPattern(`/users/?`);

      return response.data;
    } catch (error) {
      log.error(`Error migrating user ${userId} to account ${newAccountId}:`, error);
      throw error;
    }
  }
}

export const usersService = new UsersService();
export default usersService;